import { useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Барабан выбора (как в будильнике iPhone): прокрутка пальцем с «примагничиванием» к значению.
 * Нажатие на число тоже выбирает его — удобно и для мыши, и для тестов.
 */
const ITEM = 40;
const VISIBLE = 5;

export function WheelColumn({ values, value, onChange, label, suffix }: { values: number[]; value: number; onChange: (v: number) => void; label: string; suffix?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | null>(null);
  const idx = Math.max(0, values.indexOf(value));

  useLayoutEffect(() => {
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ITEM) !== idx) el.scrollTop = idx * ITEM;
  }, [idx]);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  const onScroll = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const i = Math.min(values.length - 1, Math.max(0, Math.round(el.scrollTop / ITEM)));
      if (values[i] !== value) onChange(values[i]!);
    }, 90);
  };

  return (
    <div className="wheel-col">
      <div
        ref={ref}
        className="wheel-scroll"
        style={{ height: ITEM * VISIBLE }}
        onScroll={onScroll}
        role="listbox"
        aria-label={label}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && idx < values.length - 1) onChange(values[idx + 1]!);
          if (e.key === 'ArrowUp' && idx > 0) onChange(values[idx - 1]!);
        }}
      >
        <div style={{ height: ITEM * Math.floor(VISIBLE / 2) }} />
        {values.map((v) => (
          <div
            key={v}
            role="option"
            aria-selected={v === value}
            className={`wheel-item${v === value ? ' on' : ''}`}
            style={{ height: ITEM }}
            onClick={() => {
              ref.current?.scrollTo({ top: values.indexOf(v) * ITEM, behavior: 'smooth' });
              onChange(v);
            }}
          >
            {String(v).padStart(2, '0')}
          </div>
        ))}
        <div style={{ height: ITEM * Math.floor(VISIBLE / 2) }} />
      </div>
      {suffix && <span className="wheel-suffix">{suffix}</span>}
    </div>
  );
}

/** «Через сколько»: часы/минуты или минуты/секунды — значение в секундах. */
export function DurationWheel({ seconds, onChange, mode }: { seconds: number; onChange: (s: number) => void; mode: 'ms' | 'hm' }) {
  const range = (n: number, step = 1) => Array.from({ length: Math.floor(n / step) }, (_, i) => i * step);
  if (mode === 'ms') {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return (
      <div className="wheel" data-testid="wheel">
        <div className="wheel-band" aria-hidden="true" />
        <WheelColumn label="Минуты" values={range(60)} value={m} suffix="мин" onChange={(v) => onChange(v * 60 + s)} />
        <WheelColumn label="Секунды" values={range(60)} value={s} suffix="сек" onChange={(v) => onChange(m * 60 + v)} />
      </div>
    );
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return (
    <div className="wheel" data-testid="wheel">
      <div className="wheel-band" aria-hidden="true" />
      <WheelColumn label="Часы" values={range(6)} value={h} suffix="ч" onChange={(v) => onChange(v * 3600 + m * 60)} />
      <WheelColumn label="Минуты" values={range(60, 5)} value={m - (m % 5)} suffix="мин" onChange={(v) => onChange(h * 3600 + v * 60)} />
    </div>
  );
}

export function formatIn(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h ? `${h} ч` : '', m ? `${m} мин` : '', s ? `${s} сек` : ''].filter(Boolean).join(' ') || '0 сек';
}
